import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { Map as ImmutableMap } from 'immutable'
import { Box, Typography, Alert, TextField } from '@mui/material'
import MuiButton from 'gui/components/common/MuiButton'
import { AI_CURATION_PROMPT } from 'services/prompts/prompts'
import { CollapsibleSection } from 'gui/components/common/CollapsibleSection'
import { useApi } from '../ai-chat/hooks/useApi'
import AIMetadataField from './ai-metadata-field'
import {
  useTranscriptFetcher,
  ProgressTracker,
  type ProgressStep,
  type OpenAIResponse,
  type AiToolsSharedTranscript,
} from './ai-helpers'

/**
 * Content types supported by AI metadata generation
 */
type ContentType = 'video' | 'series' | 'course'

/**
 * Field names that can be applied to content forms
 */
type ActionableFieldName =
  | 'shortDescription'
  | 'description'
  | 'thirdPartyDescription'
  | 'seoTitle'
  | 'seoDescription'
  | 'seoKeywords'
  | 'director'
  | 'producer'
  | 'writer'
  | 'studio'
  | 'instructor'

/**
 * Content-type specific actionable fields
 * Lists which AI response fields can be applied to the form for different content types
 *
 * Structure:
 * {
 *   [contentType]: [fieldName1, fieldName2, ...]
 * }
 *
 * AI response fields that match these names will be actionable (editable + applicable).
 * AI response fields NOT in this list will be display-only.
 */
const ACTIONABLE_FIELDS: Partial<Record<ContentType, ActionableFieldName[]>> = {
  video: [
    'shortDescription',
    'description',
    'thirdPartyDescription',
    'seoTitle',
    'seoDescription',
    'seoKeywords',
    'director',
    'producer',
    'writer',
    'studio',
    'instructor',
  ],
  series: [
    'shortDescription',
    'description',
    'thirdPartyDescription',
    'seoTitle',
    'seoDescription',
    'seoKeywords',
    'director',
    'producer',
    'writer',
    'studio',
    'instructor',
  ],
  // Add other content types here as needed
  // course: [...],
}

/**
 * Get actionable fields for a specific content type
 * @param contentType - The content type (e.g., 'video', 'article')
 * @returns List of actionable field names for that content type
 */
const getActionableFields = (contentType: string): ActionableFieldName[] =>
  ACTIONABLE_FIELDS[contentType as ContentType] || []

/**
 * Check if a field is actionable for the given content type
 * Supports dot notation - compares the rightmost part of the field name
 * (e.g., "seo.seoTitle" matches "seoTitle")
 * @param fieldName - The field name to check (may contain dots)
 * @param contentType - The content type
 * @returns True if the field is actionable
 */
const isFieldActionable = (fieldName: string, contentType: string): boolean => {
  const actionableFields = getActionableFields(contentType)

  // Get the rightmost part after the last dot (or the whole name if no dots)
  const fieldNamePart = fieldName.split('.').pop() as string

  // Check if the field name matches exactly, or if the rightmost part matches
  return (
    actionableFields.includes(fieldName as ActionableFieldName) ||
    actionableFields.includes(fieldNamePart as ActionableFieldName)
  )
}

/**
 * Map AI response field names to actual form field names
 * The AI returns nested objects that get flattened (e.g., seo.seoTitle),
 * but the form fields use simple names (e.g., seoTitle)
 * @param aiFieldName - The flattened field name from AI (e.g., 'seo.seoTitle')
 * @returns The actual form field name (e.g., 'seoTitle')
 */
const mapAIFieldToFormField = (aiFieldName: string): string =>
  // For nested fields, use the rightmost part after the last dot
  // e.g., 'seo.seoTitle' -> 'seoTitle'
  // For simple fields, return as-is
  // e.g., 'thirdPartyDescription' -> 'thirdPartyDescription'
  aiFieldName.split('.').pop() as string

/**
 * Flatten nested objects to handle dot notation keys (like seo.seoTitle)
 * @param obj - Object to flatten
 * @param prefix - Current path prefix
 * @returns Flattened object with dot notation keys
 */
const flattenObject = (obj: Record<string, unknown>, prefix = ''): Record<string, unknown> => {
  const flattened: Record<string, unknown> = {}
  Object.keys(obj).forEach((key) => {
    const value = obj[key]
    const fullKey = prefix ? `${prefix}.${key}` : key

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Recurse into nested objects
      Object.assign(flattened, flattenObject(value as Record<string, unknown>, fullKey))
    } else {
      // Store the value with its full path
      flattened[fullKey] = value
    }
  })
  return flattened
}

/**
 * Component props
 */
interface AIMetadataGenerationProps {
  /** Immutable Map containing content data */
  content: ImmutableMap<string, unknown>
  /** Whether the form is in view-only mode */
  isViewOnlyMode: boolean
  /** ID of the attached media */
  featureMediaId?: number | string | null
  /** Formik function to set form field values */
  setFieldValue: (field: string, value: unknown) => void
  /**
   * When provided (same object passed to AITranscript), Generate uses the edited draft from the
   * Transcript step instead of re-fetching the server transcript.
   */
  sharedTranscript?: AiToolsSharedTranscript
}

type AIMetadataGenerationCoreProps = Omit<AIMetadataGenerationProps, 'sharedTranscript'> &
  AiToolsSharedTranscript

/**
 * AI Metadata Generation — core implementation (transcript props supplied by parent or local wrapper).
 */
function AIMetadataGenerationCore ({
  content,
  isViewOnlyMode,
  featureMediaId,
  setFieldValue,
  fetchTranscript,
  fetchedTranscript,
  transcriptLoading,
  transcriptError,
  transcriptDraft,
  onTranscriptDraftChange,
}: AIMetadataGenerationCoreProps) {
  const { makeRequest } = useApi()
  /** True only when Generate Metadata (empty draft) started the GraphQL fetch — not AITranscript's Fetch */
  const pendingMetadataAfterTranscriptFetchRef = useRef(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [generatedMetadata, setGeneratedMetadata] = useState<Record<string, unknown> | null>(null)
  const [appliedFields, setAppliedFields] = useState<Record<string, boolean>>({})
  const [promptExpanded, setPromptExpanded] = useState<boolean>(false)
  const [transcriptExpanded, setTranscriptExpanded] = useState<boolean>(false)
  const [editablePrompt, setEditablePrompt] = useState<string>(AI_CURATION_PROMPT)
  const [currentStep, setCurrentStep] = useState<ProgressStep | null>(null)
  const [completedSteps, setCompletedSteps] = useState<ProgressStep[]>([])
  const [editableValues, setEditableValues] = useState<Record<string, string>>({})

  // Get content type from content
  const contentType = (content.get('contentType') as string | undefined) || 'video'

  /**
   * Flatten the generated metadata for display
   * Converts nested objects like { seo: { seoTitle: 'x' } } to { 'seo.seoTitle': 'x' }
   */
  const flattenedMetadata = useMemo(() => {
    if (!generatedMetadata) return null
    return flattenObject(generatedMetadata)
  }, [generatedMetadata])

  /**
   * Generate AI metadata from transcript
   * Calls OpenAI via Brooklyn's proxy endpoint with the transcript and AI curation prompt
   */
  const generateAIMetadata = useCallback(
    async (transcriptText: string): Promise<void> => {
      try {
        // Build the full user prompt by combining the editable prompt with the transcript
        const userPrompt = `${editablePrompt}

---

### **TRANSCRIPT TO ANALYZE**

${transcriptText}

---

**IMPORTANT REMINDER**: Return ONLY valid JSON with ALL required fields from the schema. Do not omit any fields.`

        // Call OpenAI via Brooklyn's proxy endpoint
        // This uses the /openai/* endpoint which forwards to OpenAI's API
        const response = (await makeRequest('/openai/v1/chat/completions', {
          body: {
            model: 'gpt-4o',
            messages: [
              {
                role: 'system',
                content:
                  'You are a content curation expert for Gaia. You analyze video transcripts and return structured JSON metadata. You MUST return valid JSON only, with no markdown formatting or code blocks.',
              },
              {
                role: 'user',
                content: userPrompt,
              },
            ],
            temperature: 0.7,
            response_format: { type: 'json_object' },
          },
        })) as unknown as OpenAIResponse

        // Parse the OpenAI response
        // OpenAI returns: { choices: [{ message: { content: "..." } }] }
        let parsedMetadata: Record<string, unknown>
        try {
          const aiContent = response?.choices?.[0]?.message?.content

          if (!aiContent) {
            throw new Error('No content in OpenAI response')
          }

          parsedMetadata = JSON.parse(aiContent) as Record<string, unknown>
        } catch (parseErr) {
          const errorMessage = parseErr instanceof Error ? parseErr.message : 'Unknown error'
          throw new Error(`AI returned invalid JSON: ${errorMessage}`)
        }

        setGeneratedMetadata(parsedMetadata)
        setCompletedSteps((prev) => [...prev, 'generating'])
        setCurrentStep('complete')
        setLoading(false)

        // Initialize editable values for actionable fields
        const flattened = flattenObject(parsedMetadata)
        const initialEditableValues: Record<string, string> = {}

        Object.entries(flattened).forEach(([key, value]) => {
          if (isFieldActionable(key, contentType)) {
            // This field is actionable - make it editable
            if (Array.isArray(value)) {
              initialEditableValues[key] = value.join(', ')
            } else if (value) {
              initialEditableValues[key] = String(value)
            }
          }
        })

        setEditableValues(initialEditableValues)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error'
        setError(`Failed to generate AI metadata: ${errorMessage}`)
        setLoading(false)
        setCurrentStep(null)
      }
    },
    [editablePrompt, contentType, makeRequest],
  )

  // After GraphQL transcript fetch completes, run AI only when Generate Metadata triggered the fetch (not Transcript-only fetch)
  useEffect(() => {
    if (!fetchedTranscript && !transcriptError && !transcriptLoading) return

    const processTranscript = async () => {
      if (transcriptError) {
        if (pendingMetadataAfterTranscriptFetchRef.current) {
          setError(transcriptError)
          setLoading(false)
          setCurrentStep(null)
          pendingMetadataAfterTranscriptFetchRef.current = false
        }
        return
      }

      if (fetchedTranscript) {
        if (!pendingMetadataAfterTranscriptFetchRef.current) {
          return
        }
        pendingMetadataAfterTranscriptFetchRef.current = false
        setCompletedSteps((prev) => [...prev, 'fetching'])
        setCurrentStep('generating')
        await generateAIMetadata(fetchedTranscript)
      }
    }

    processTranscript()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedTranscript, transcriptError, transcriptLoading])

  /**
   * Handle generate metadata button click
   * Fetches the transcript when none is loaded yet; otherwise uses the editable transcript as-is
   */
  const handleGenerateMetadata = (): void => {
    const contentId = content.get('contentId') as number | string | undefined

    if (!contentId) {
      setError('Content ID not found')
      return
    }

    // Clear previous errors and data
    setError(null)
    setGeneratedMetadata(null)
    setLoading(true)

    if (transcriptDraft.trim()) {
      pendingMetadataAfterTranscriptFetchRef.current = false
      setCompletedSteps(['fetching'])
      setCurrentStep('generating')
      generateAIMetadata(transcriptDraft).catch(() => undefined)
      return
    }

    pendingMetadataAfterTranscriptFetchRef.current = true
    onTranscriptDraftChange('')
    setCurrentStep('fetching')
    setCompletedSteps([])
    fetchTranscript(contentId)
  }

  /**
   * Handle retry button click
   * Clears error and retries the operation
   */
  const handleRetry = (): void => {
    setError(null)
    handleGenerateMetadata()
  }

  /**
   * Apply a metadata field to the form
   * @param fieldName - The AI field name (e.g., 'seo.seoTitle', 'thirdPartyDescription')
   * @param editableKey - The key in editableValues state
   */
  const handleApplyMetadata = (fieldName: string, editableKey: string): void => {
    const value = editableValues[editableKey]
    if (!value || isViewOnlyMode) return

    try {
      // Map the AI field name to the actual form field name
      // e.g., 'seo.seoTitle' -> 'seoTitle'
      const formFieldName = mapAIFieldToFormField(fieldName)

      setFieldValue(formFieldName, value)

      // Mark this field as applied for visual feedback (using original fieldName for the key)
      setAppliedFields((prev) => ({ ...prev, [fieldName]: true }))

      // Clear the "applied" state after 2 seconds
      setTimeout(() => {
        setAppliedFields((prev) => ({ ...prev, [fieldName]: false }))
      }, 2000)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to apply metadata: ${errorMessage}`)
    }
  }

  // Determine if button should be disabled
  const isButtonDisabled = !featureMediaId || isViewOnlyMode || loading

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h6" gutterBottom>
        AI Metadata Generation
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Generate metadata using the prompt below and the transcript from the Transcript section
        above (including any edits you made there). You can refine the transcript again in the
        collapsible below before re-running.
      </Typography>

      {/* Collapsible Prompt Editor */}
      <CollapsibleSection
        title="Prompt"
        expanded={promptExpanded}
        onToggle={() => setPromptExpanded(!promptExpanded)}
      >
        <Box sx={{ p: 2, pt: 0 }}>
          <TextField
            fullWidth
            multiline
            rows={12}
            value={editablePrompt}
            onChange={(e) => setEditablePrompt(e.target.value)}
            disabled={loading || isViewOnlyMode}
            variant="outlined"
            sx={{
              backgroundColor: '#fff',
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.85rem',
              },
            }}
          />
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <MuiButton
              size="small"
              variant="outlined"
              onClick={() => setEditablePrompt(AI_CURATION_PROMPT)}
              disabled={loading || isViewOnlyMode}
            >
              Reset to Default
            </MuiButton>
          </Box>
        </Box>
      </CollapsibleSection>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <MuiButton color="primary" size="small" onClick={handleRetry}>
              Retry
            </MuiButton>
          }
        >
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <MuiButton
          variant="contained"
          color="primary"
          onClick={handleGenerateMetadata}
          disabled={isButtonDisabled}
        >
          Generate Metadata from AI
        </MuiButton>
      </Box>

      {/* Progress Tracker */}
      {(loading || currentStep) && (
        <ProgressTracker
          steps={[
            { key: 'fetching', label: 'Fetching Transcript' },
            {
              key: 'generating',
              label: 'Generating Metadata',
              loadingLabel: 'Generating Metadata (this takes a moment...)',
            },
          ]}
          currentStep={currentStep}
          completedSteps={completedSteps}
        />
      )}

      {/* Collapsible transcript editor — same draft as Transcript section when shared */}
      {(Boolean(fetchedTranscript) || transcriptDraft.length > 0) && (
        <CollapsibleSection
          title={`Transcript (${transcriptDraft.length.toLocaleString()} characters)`}
          expanded={transcriptExpanded}
          onToggle={() => setTranscriptExpanded(!transcriptExpanded)}
        >
          <Box sx={{ p: 2, pt: 0 }}>
            <TextField
              fullWidth
              multiline
              rows={12}
              value={transcriptDraft}
              onChange={(e) => onTranscriptDraftChange(e.target.value)}
              disabled={loading || isViewOnlyMode}
              variant="outlined"
              sx={{
                backgroundColor: '#fff',
                '& .MuiInputBase-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                },
              }}
            />
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              <MuiButton
                size="small"
                variant="outlined"
                onClick={() => onTranscriptDraftChange(fetchedTranscript ?? '')}
                disabled={loading || isViewOnlyMode}
              >
                Reset to Original
              </MuiButton>
            </Box>
          </Box>
        </CollapsibleSection>
      )}

      {flattenedMetadata && (
        <Box
          sx={{
            mt: 3,
            p: 3,
            backgroundColor: '#f5f5f5',
            borderRadius: 2,
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="h6" gutterBottom>
            Generated AI Metadata
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Fields matching <strong>{contentType}</strong> form fields can be edited and applied.
            Other fields are shown for reference.
          </Typography>

          {/* Render all fields - each field decides if it's actionable or display-only */}
          {Object.entries(flattenedMetadata).map(([key, value]) => {
            if (!value && value !== 0) return null // Skip null/undefined/empty (but keep 0)

            const isActionable = isFieldActionable(key, contentType)

            return (
              <AIMetadataField
                key={key}
                fieldKey={key}
                value={value}
                isActionable={isActionable}
                isViewOnlyMode={isViewOnlyMode}
                editableValues={editableValues}
                setEditableValues={setEditableValues}
                onApply={handleApplyMetadata}
                appliedFields={appliedFields}
              />
            )
          })}

          {/* Raw JSON for debugging */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Full metadata (for debugging):
            </Typography>
            <Box
              component="pre"
              sx={{
                backgroundColor: '#fff',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: 300,
                fontSize: '0.75rem',
                border: '1px solid #ddd',
              }}
            >
              {JSON.stringify(generatedMetadata, null, 2)}
            </Box>
          </Box>
        </Box>
      )}

      {!featureMediaId && !isViewOnlyMode && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Please attach media to this video before generating metadata.
        </Alert>
      )}
    </Box>
  )
}

function AIMetadataGenerationWithLocalTranscript (
  props: Omit<AIMetadataGenerationProps, 'sharedTranscript'>,
) {
  const fetcher = useTranscriptFetcher()
  const [draft, setDraft] = useState('')

  useEffect(() => {
    if (fetcher.transcript != null && fetcher.transcript !== '') {
      setDraft(fetcher.transcript)
    }
  }, [fetcher.transcript])

  const transcriptSlot: AiToolsSharedTranscript = {
    fetchTranscript: fetcher.fetchTranscript,
    fetchedTranscript: fetcher.transcript,
    transcriptLoading: fetcher.loading,
    transcriptError: fetcher.error,
    transcriptDraft: draft,
    onTranscriptDraftChange: setDraft,
  }

  return <AIMetadataGenerationCore {...props} {...transcriptSlot} />
}

function AIMetadataGeneration (props: AIMetadataGenerationProps) {
  const { sharedTranscript, ...rest } = props
  if (sharedTranscript) {
    return <AIMetadataGenerationCore {...rest} {...sharedTranscript} />
  }
  return <AIMetadataGenerationWithLocalTranscript {...rest} />
}

export default AIMetadataGeneration
